import { useEffect, useState } from 'react';
import { ExternalLink, FileText, Loader2, Mail, Printer, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import { useSendLetter } from '@/hooks/admin/useSendLetter';
import { usePaperInvoice } from '@/hooks/admin/usePaperInvoice';
import {
  useOpenInvoices,
  useSendInvoice,
  type AdminOpenInvoice,
} from '@/hooks/admin/useSendInvoice';
import { PaperStatementAction } from './billing-comms';

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { dateStyle: 'medium' });
  } catch {
    return s;
  }
}

export default function PaperTab() {
  const { customer } = useCustomerDetailContext();
  return (
    <div className="space-y-5 max-w-2xl">
      <p className="text-sm text-brix-muted">
        Mail a physical open-items statement or a single invoice to {customer.name} via DocuPost,
        or email a chosen invoice. Previewing a PDF is free; postage is spent only when you confirm
        “Send for real”.
      </p>
      <PaperStatementAction customerId={customer.id} customerName={customer.name} />
      <SendInvoiceAction
        customerId={customer.id}
        customerName={customer.name}
        defaultChannel={customer.billing_doc_type === 'invoice' ? 'paper' : 'email'}
      />
      <SendLetterCard customerId={customer.id} customerName={customer.name} />
    </div>
  );
}

/**
 * SendInvoiceAction — pick one of the customer's OPEN invoices, choose a
 * channel (Email or Paper), then send. Email sends immediately (confirm-gated);
 * Paper is preview-first (free PDF) → confirm-gated "Send for real" (postage).
 * The default channel honors the customer's billing_doc_type but the admin can
 * pick either at send time.
 */
function SendInvoiceAction({
  customerId,
  customerName,
  defaultChannel,
}: {
  customerId: string;
  customerName: string;
  defaultChannel: 'email' | 'paper';
}) {
  const open = useOpenInvoices(customerId);
  const emailMut = useSendInvoice();
  const paperMut = usePaperInvoice();

  const [selectedId, setSelectedId] = useState<string>('');
  const [channel, setChannel] = useState<'email' | 'paper'>(defaultChannel);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Default the selection to the first open invoice once loaded.
  useEffect(() => {
    if (!selectedId && open.data && open.data.length > 0) {
      setSelectedId(open.data[0]!.qbo_invoice_id);
    }
  }, [open.data, selectedId]);

  const invoices: AdminOpenInvoice[] = open.data ?? [];
  const selected = invoices.find((i) => i.qbo_invoice_id === selectedId) ?? null;

  const previewing = paperMut.isPending && paperMut.variables?.mode === 'preview';
  const sendingPaper = paperMut.isPending && paperMut.variables?.mode === 'send';
  const sendingEmail = emailMut.isPending;
  const busy = previewing || sendingPaper || sendingEmail;
  const errorMsg =
    (emailMut.error instanceof Error ? emailMut.error.message : null) ??
    (paperMut.error instanceof Error ? paperMut.error.message : null);

  const resetTransient = () => {
    setPreviewUrl(null);
    setDone(null);
  };

  const handleChannel = (c: 'email' | 'paper') => {
    setChannel(c);
    resetTransient();
  };
  const handleSelect = (id: string) => {
    setSelectedId(id);
    resetTransient();
  };

  const handlePaperPreview = () => {
    if (!selectedId) return;
    setDone(null);
    paperMut.mutate(
      { customer_id: customerId, qbo_invoice_id: selectedId, mode: 'preview' },
      {
        onSuccess: (res) => {
          setPreviewUrl(res.pdf_url);
          if (typeof window !== 'undefined') window.open(res.pdf_url, '_blank', 'noopener');
        },
      },
    );
  };

  const handlePaperSend = () => {
    if (!selectedId) return;
    if (
      !window.confirm(
        `Mail invoice ${selected?.doc_number ?? selectedId} to ${customerName} via DocuPost? ` +
          `This spends real postage and cannot be undone.`,
      )
    ) {
      return;
    }
    paperMut.mutate(
      { customer_id: customerId, qbo_invoice_id: selectedId, mode: 'send' },
      {
        onSuccess: (res) => {
          if (res.mode === 'send' && res.ok) setDone('Mailed via DocuPost.');
        },
      },
    );
  };

  const handleEmailSend = () => {
    if (!selectedId) return;
    if (
      !window.confirm(
        `Email invoice ${selected?.doc_number ?? selectedId} to ${customerName}'s invoice recipients now?`,
      )
    ) {
      return;
    }
    emailMut.mutate(
      { customer_id: customerId, qbo_invoice_id: selectedId },
      {
        onSuccess: (res) => {
          setDone(
            res.sent
              ? `Emailed to ${res.recipients.join(', ') || 'invoice recipients'}.`
              : `Not sent — ${res.skipped_reason ?? 'no recipient'}.`,
          );
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText size={14} className="text-brix-primary" />
        <span className="text-sm font-semibold text-brix-ink">Send an invoice</span>
        <span className="text-[11px] text-brix-muted">Email or mailed via DocuPost</span>
      </div>

      {open.isLoading ? (
        <div className="text-sm text-brix-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading open invoices…
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-brix-muted">No open invoices for {customerName}.</p>
      ) : (
        <>
          {/* Invoice picker */}
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Open invoice
            </label>
            <select
              value={selectedId}
              onChange={(e) => handleSelect(e.target.value)}
              className="mt-1 w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink focus:outline-none focus:ring-2 focus:ring-brix-primary/40"
            >
              {invoices.map((inv) => (
                <option key={inv.qbo_invoice_id} value={inv.qbo_invoice_id}>
                  #{inv.doc_number} · {fmtDate(inv.txn_date)} · {fmtUsd(inv.balance)} due
                </option>
              ))}
            </select>
          </div>

          {/* Channel toggle */}
          <div className="flex items-center gap-2">
            <ChannelChip
              active={channel === 'email'}
              icon={<Mail size={12} />}
              label="Email"
              onClick={() => handleChannel('email')}
            />
            <ChannelChip
              active={channel === 'paper'}
              icon={<Printer size={12} />}
              label="Paper"
              onClick={() => handleChannel('paper')}
            />
          </div>

          <p className="text-[11px] text-brix-muted">
            {channel === 'email'
              ? 'Emails the branded invoice to the customer’s invoice recipients.'
              : 'Preview is free. Postage is only spent when you click “Send for real”.'}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {channel === 'email' ? (
              <Button variant="primary" size="sm" onClick={handleEmailSend} disabled={busy || !selectedId}>
                {sendingEmail ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {sendingEmail ? 'Sending…' : 'Email invoice'}
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePaperPreview}
                  disabled={busy || !selectedId}
                >
                  {previewing ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
                  {previewing ? 'Rendering…' : 'Preview PDF'}
                </Button>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brix-primary hover:underline"
                  >
                    <ExternalLink size={12} /> Open preview
                  </a>
                )}
                {previewUrl && !done && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handlePaperSend}
                    disabled={busy}
                    className="text-red-50 bg-red-600 hover:bg-red-700"
                    title="Mail this invoice via DocuPost (spends postage)"
                  >
                    {sendingPaper ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {sendingPaper ? 'Mailing…' : 'Send for real'}
                  </Button>
                )}
              </>
            )}
          </div>
        </>
      )}

      {done && <div className="text-xs text-emerald-700">{done}</div>}
      {errorMsg && <div className="text-xs text-red-600">{errorMsg}</div>}
    </div>
  );
}

function ChannelChip({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs cursor-pointer transition-all',
        active
          ? 'border-brix-primary bg-brix-primary-tint text-brix-primary font-semibold'
          : 'border-brix-border bg-brix-surface text-brix-ink hover:border-brix-primary/40',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * SendLetterCard — free-form DocuPost letter. Title + multiline body, a free
 * Preview (renders + opens the PDF, no mail), then a confirm-gated "Send for
 * real" that spends postage. Mirrors PaperStatementAction's preview-first
 * safety. Mails regardless of the customer's paper opt-in flags.
 */
function SendLetterCard({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const mutation = useSendLetter();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const previewing = mutation.isPending && mutation.variables?.mode === 'preview';
  const sending = mutation.isPending && mutation.variables?.mode === 'send';
  const errorMsg = mutation.error instanceof Error ? mutation.error.message : null;
  const canSubmit = title.trim().length > 0 && body.trim().length > 0;

  const handlePreview = () => {
    if (!canSubmit) return;
    setSent(false);
    mutation.mutate(
      { customer_id: customerId, title: title.trim(), body: body.trim(), mode: 'preview' },
      {
        onSuccess: (res) => {
          setPreviewUrl(res.pdf_url);
          if (typeof window !== 'undefined') window.open(res.pdf_url, '_blank', 'noopener');
        },
      },
    );
  };

  const handleSend = () => {
    if (
      !window.confirm(
        `Mail this letter to ${customerName} via DocuPost? This spends real postage and cannot be undone.`,
      )
    ) {
      return;
    }
    mutation.mutate(
      { customer_id: customerId, title: title.trim(), body: body.trim(), mode: 'send' },
      {
        onSuccess: (res) => {
          if (res.mode === 'send' && res.ok) setSent(true);
        },
      },
    );
  };

  // Editing after a preview invalidates the rendered PDF — clear it.
  const onEdit = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPreviewUrl(null);
    setSent(false);
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Mail size={14} className="text-brix-primary" />
        <span className="text-sm font-semibold text-brix-ink">Send a letter</span>
        <span className="text-[11px] text-brix-muted">DocuPost · mailed USPS first class</span>
      </div>
      <p className="text-[11px] text-brix-muted">
        A free-form one-off letter to {customerName}. Preview is free. Postage is only spent when you
        click “Send for real”. This mails regardless of the customer's paper preferences.
      </p>

      <div>
        <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
          Title
        </label>
        <Input
          value={title}
          onChange={(e) => onEdit(setTitle)(e.target.value)}
          placeholder="e.g. Notice of upcoming delivery schedule change"
          className="mt-1"
        />
      </div>

      <div>
        <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
          Body
        </label>
        <textarea
          value={body}
          onChange={(e) => onEdit(setBody)(e.target.value)}
          placeholder="Write your letter here. Leave a blank line between paragraphs."
          rows={8}
          className="mt-1 w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted focus:outline-none focus:ring-2 focus:ring-brix-primary/40"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePreview}
          disabled={!canSubmit || mutation.isPending}
        >
          {previewing ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
          {previewing ? 'Rendering…' : 'Preview'}
        </Button>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-brix-primary hover:underline"
          >
            <ExternalLink size={12} /> Open preview
          </a>
        )}
        {previewUrl && !sent && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={mutation.isPending}
            className="text-red-50 bg-red-600 hover:bg-red-700"
            title="Mail this letter via DocuPost (spends postage)"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {sending ? 'Mailing…' : 'Send for real'}
          </Button>
        )}
      </div>

      {sent && <div className="text-xs text-emerald-700">Mailed via DocuPost.</div>}
      {errorMsg && <div className="text-xs text-red-600">{errorMsg}</div>}
    </div>
  );
}
